import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import Header from "../../components/home/Header";
import Nav from "../../components/home/Nav";
import { useFilterProductsQuery } from "../../store/services/homeProducts";
import ProductCard from "../../components/home/ProductCard";
import Pagination from "../../components/Pagination";
import ProductSkeleton from "../../components/home/ProductSkeleton";
import ProductFilters from "../../components/home/ProductFilters";
import MobileAppendProducts from "../../components/home/MobileAppendProducts";
import { toPersianNumber } from "../../utils/persian";

const popularSearchLinks = [
  { title: "کفش اسپرت زنانه", url: "/search-products/کفش اسپرت زنانه/1" },
  { title: "صندل زنانه", url: "/search-products/صندل زنانه/1" },
  { title: "کفش مجلسی زنانه", url: "/search-products/کفش مجلسی زنانه/1" },
  { title: "کفش پرسنلی زنانه", url: "/search-products/کفش پرسنلی زنانه/1" },
  { title: "ونس زنانه", url: "/search-products/ونس زنانه/1" }
];

const getInitialFilters = (search, page) => {
  const params = new URLSearchParams(search);

  return {
    q: "",
    category: params.get("category") || "",
    fixedCategory: "",
    brand: params.get("brand") || "",
    color: params.get("color") || "",
    size: params.get("size") || "",
    min_price: params.get("min_price") || "",
    max_price: params.get("max_price") || "",
    available: params.get("available") || "",
    sale: params.get("sale") || "",
    sort: params.get("sort") || "latest",
    page: parseInt(page)
  };
};

const AllProducts = () => {
  const { page = 1 } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [filters, setFilters] = useState(() => getInitialFilters(location.search, page));

  useEffect(() => {
    const params = new URLSearchParams();

    ["category", "brand", "color", "size", "min_price", "max_price", "available", "sale", "sort"].forEach((key) => {
      if (filters[key] && !(key === "sort" && filters[key] === "latest")) {
        params.set(key, filters[key]);
      }
    });

    const query = params.toString();
    const nextPage = parseInt(page) || 1;
    const url = `/products/${nextPage}${query ? `?${query}` : ""}`;

    if (url !== `${location.pathname}${location.search}`) {
      navigate(url, { replace: true });
    }
  }, [filters, page, navigate, location.pathname, location.search]);

  const queryParams = useMemo(
    () => ({
      category: filters.category,
      brand: filters.brand,
      color: filters.color,
      size: filters.size,
      min_price: filters.min_price,
      max_price: filters.max_price,
      available: filters.available,
      sale: filters.sale,
      sort: filters.sort,
      page: parseInt(page)
    }),
    [filters, page]
  );

  const { data, isFetching } = useFilterProductsQuery(queryParams);

  return (
    <>
      <Nav />

      <div className="mt-[76px]">
        <Header>{filters.sale ? "فروش ویژه" : "همه محصولات"}</Header>
      </div>

      <section className="shop-page">
        <div className="site-container">
          <div className="shop-list-topbar">
            <div className="shop-result-meta">
              <span>{toPersianNumber(data?.count || 0)} محصول</span>
              <strong>{filters.sale ? "محصولات فروش ویژه" : "همه محصولات جوتی"}</strong>
              <p>{filters.sale ? "محصولات انتخاب‌شده برای کمپین فعال فروش ویژه." : "محصولات را با فیلتر دسته‌بندی، برند، رنگ، سایز و قیمت دقیق‌تر کن."}</p>

              {!filters.sale && (
                <div className="shop-popular-searches" aria-label="جستجوهای محبوب">
                  <span>جستجوهای محبوب:</span>
                  {popularSearchLinks.map((item) => (
                    <Link to={item.url} key={item.title}>
                      {item.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <select
              aria-label="مرتب‌سازی محصولات"
              value={filters.sort}
              onChange={(e) => setFilters((prev) => ({ ...prev, sort: e.target.value, page: 1 }))}
            >
              <option value="latest">جدیدترین</option>
              <option value="cheapest">ارزان‌ترین</option>
              <option value="expensive">گران‌ترین</option>
              <option value="popular">محبوب‌ترین</option>
              <option value="discount">بیشترین تخفیف</option>
            </select>
          </div>

          <div className="product-list-layout">
            <ProductFilters
              filters={filters}
              setFilters={setFilters}
              filterOptions={data?.filters || {}}
              count={data?.count || 0}
              open={mobileFilterOpen}
              setOpen={setMobileFilterOpen}
            />

            <section className="product-list-content">
              {isFetching ? (
                <ProductSkeleton />
              ) : data?.count > 0 ? (
                <>
                  <div className="product-grid-desktop">
                    <div className="product-grid">
                      {data.products.map((product, index) => (
                        <ProductCard product={product} key={product._id || product.id} index={index} priority={index < 4} />
                      ))}
                    </div>

                    <Pagination page={parseInt(page)} perPage={data.perPage} count={data.count} path="products" />
                  </div>

                  <MobileAppendProducts
                    initialProducts={data.products}
                    count={data.count}
                    perPage={data.perPage}
                    queryParams={queryParams}
                    resetKey={`all-${JSON.stringify(filters)}`}
                  />
                </>
              ) : (
                <div className="shop-empty-state">
                  <strong>محصولی پیدا نشد</strong>
                  <p>با فیلترهای فعلی محصولی وجود ندارد. فیلترها را پاک کن یا مقدارها را تغییر بده.</p>
                </div>
              )}
            </section>
          </div>
        </div>
      </section>
    </>
  );
};

export default AllProducts;
